import type { SceneId } from "@/scenes/sceneId";

import type { RendererPreference } from "./parseEnv";
import { parseBoolean, parseNumber, parseRenderer } from "./parseEnv";

export type { RendererPreference } from "./parseEnv";

export interface GameConfig {
  /** Sélecteur CSS du `<canvas>` de rendu. */
  readonly canvasSelector: string;
  readonly renderer: RendererPreference;
  readonly antialias: boolean;
  /**
   * Plafond du `devicePixelRatio`. Au-delà, le coût en fragments explose sans
   * gain visuel notable — 2 est un bon compromis sur écrans HiDPI.
   */
  readonly maxPixelRatio: number;
  /**
   * Delta maximal (en secondes) transmis à `update()`. Après un changement
   * d'onglet, le premier frame peut représenter plusieurs secondes : sans
   * plafond, la simulation fait un bond (traversée de murs, téléportations).
   */
  readonly maxDeltaSeconds: number;
  /** Autorise la bascule de l'inspecteur Babylon (effectif en dev seulement). */
  readonly enableInspector: boolean;
  /** Scène chargée au démarrage. */
  readonly startScene: SceneId;
}

/**
 * Configuration par défaut, surchargeable par variables d'environnement Vite.
 * Voir `.env.example`.
 */
export const gameConfig: GameConfig = {
  canvasSelector: "#render-canvas",
  renderer: parseRenderer(import.meta.env.VITE_RENDERER, "auto"),
  antialias: parseBoolean(import.meta.env.VITE_ANTIALIAS, true),
  maxPixelRatio: parseNumber(import.meta.env.VITE_MAX_PIXEL_RATIO, 2),
  // 1/15e de seconde : au-delà, mieux vaut un ralenti qu'un saut de simulation.
  maxDeltaSeconds: 1 / 15,
  enableInspector: parseBoolean(import.meta.env.VITE_ENABLE_INSPECTOR, true),
  startScene: "main",
};

/** Applique des surcharges ponctuelles (tests, scénarios de debug). */
export function withOverrides(overrides: Partial<GameConfig>): GameConfig {
  return { ...gameConfig, ...overrides };
}
